import Config from 'src/core/utils/Config';
import type { MediaLibraryServiceContract } from 'src/data/services/types/MediaLibraryServiceTypes';

/**
 * Saving a photo from a browser.
 *
 * A browser has no photo library, but it does have a downloads folder -- it just cannot be handed a
 * cross-origin image and told to save it. `<a download>` is ignored for one, and the web app never
 * shares an origin with the photos: locally :4175 against :8000, in production
 * app.topicusevents.nl against the apex or Cloudinary. That is why the button used to open the
 * picture in a new tab and leave the saving to the visitor.
 *
 * So the photo is fetched from the API, which answers it with a Content-Disposition, and the bytes
 * are handed to the browser as a blob. Fetching rather than navigating is what makes a refusal
 * visible: navigating to a 404 saves nothing and says nothing, and this would still have to report
 * success. Now it rejects, and the error message the caller already has does its work.
 *
 * A sibling rather than a Platform.OS branch, because this keeps expo-media-library out of the web
 * bundle entirely: it throws while being imported, so guarding at the call site is too late.
 */

/** `attachment; filename=a.jpg` and `attachment; filename="a.jpg"` both occur -- see the tests. */
function nameFromDisposition(disposition: string | null): string | null {
	const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition ?? '');

	return match?.[1]?.trim() ?? null;
}

/** Last resort: the photo's own file name, so a download is never called "download". */
function nameFromUrl(uri: string): string {
	const path = uri.split('?')[0] ?? '';

	return path.split('/').filter(Boolean).pop() ?? 'foto.jpg';
}

const MediaLibraryServiceWeb: MediaLibraryServiceContract = {
	// Nothing to grant in a browser, and the caller only saves once permission is given.
	requestPermission(): Promise<boolean> {
		return Promise.resolve(true);
	},

	async saveToLibrary(uri: string): Promise<void> {
		const base = Config.apiUrl.replace(/\/$/, '');

		// Without a configured API url the request would go to the web app's own origin, where the
		// SPA fallback answers with the app itself -- a "download" of an html page. Refuse instead.
		if (base === '') {
			throw new Error('No API url configured, so there is nowhere to ask for the photo');
		}

		const response = await fetch(`${base}/photos/download?url=${encodeURIComponent(uri)}`);

		if (!response.ok) {
			throw new Error(`The photo could not be downloaded (${response.status})`);
		}

		const objectUrl = URL.createObjectURL(await response.blob());

		try {
			const anchor = document.createElement('a');

			anchor.href = objectUrl;
			anchor.download = nameFromDisposition(response.headers.get('content-disposition')) ?? nameFromUrl(uri);
			anchor.style.display = 'none';

			document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
		} finally {
			URL.revokeObjectURL(objectUrl);
		}
	},
};

export default MediaLibraryServiceWeb;
